import { useEffect, useRef } from 'react';
import { createFileRoute, notFound, redirect } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'use-intl';
import { handleToUrlSegment, isMemberRef, isUuid } from '@leanin/shared';
import { asUserId, type UserId } from '@leanin/shared/ids';
import { trackProfileViewed } from '@/lib/analytics/events';
import type { PageIdLoaderData } from '@/lib/analytics/page-meta';
import { useMember } from '@/lib/use-session';
import {
  mapProfileViewToProfile,
  memberSinceFromCreatedAt,
} from '@/components/profile/mapper';
import {
  ProfileChrome,
  type ProfileHeaderData,
} from '@/components/profile/ProfileChrome';
import { ProfileChromeSkeleton } from '@/components/profile/ProfileChromeSkeleton';
import { ProfileContentView } from '@/components/profile/ProfileContentView';
import { ErrorState, useDelayedLoading } from '@leanin/ui';
import { RetryButton } from '@/components/states/index';
import { NotFoundPage } from '@/components/shell/NotFoundPage';
import { isNotFoundApiError } from '@/lib/api-error';
import { identityKeys, type MemberView } from '@/lib/queries/identity';
import { groupKeys } from '@/lib/queries/groups';
import { useInfiniteAuthorPosts } from '@/lib/queries/feed';
import { authoredPostToFeedPost } from '@/components/feed/feed-item-to-post';
import { useFormatDate } from '@/lib/format-date.client';
import type { Profile } from '@/components/profile/types';

export const Route = createFileRoute('/_authed/members/$id')({
  beforeLoad: ({ params }) => {
    // The segment is the member's handle in URL form (the address-bar
    // form) or their uuid (what every in-app link still emits). Anything
    // else — including a reserved route word like `me` or `facets` —
    // cannot address a member, so 404 before any fetch. Same predicate as
    // the server's param schema, and case-insensitive like it:
    // `users.handle` is citext, so `/members/Kelly-Cuesta` resolves
    // server-side and must not 404 here first. The loader canonicalises it.
    if (!isMemberRef(params.id)) throw notFound();
  },
  // Canonical URL: the handle is the address-bar form, so what a member
  // copies out of the bar is readable. In-app links still emit uuids
  // (handle is absent from the person-shaped view types they render
  // from), so a uuid arrival — and a capitalised handle — is redirected to
  // the handle URL here, before the navigation commits. A `navigate`
  // after mount would commit the uuid location first and the handle
  // location second, and the GA4 `page_viewed` subscriber counts committed
  // navigations, so every in-app profile click would count twice.
  loader: async ({ params, context: { queryClient } }): Promise<PageIdLoaderData> => {
    const member = await resolveMember(queryClient, params.id.toLowerCase());
    // Nothing resolved: the 404 or failure is in the cache for the page's
    // own query to render (NotFoundPage, or the retry surface).
    if (member === undefined) return {};
    const segment = handleToUrlSegment(member.handle);
    if (segment !== params.id) {
      // Seed the canonical key so the redirected mount renders synchronously.
      queryClient.setQueryData(identityKeys.memberRef(segment).queryKey, member);
      throw redirect({
        to: '/members/$id',
        params: { id: segment },
        search: true,
        hash: true,
        replace: true,
      });
    }
    // The URL carries the handle; analytics keeps the uuid as page_id.
    return { pageId: member.id };
  },
  // Hover preload would run this loader, and with it a profile fetch, for
  // every member link the pointer crosses in a directory or feed. The
  // route chunk still preloads; the data waits for the click.
  preload: false,
  pendingComponent: ProfileChromeSkeleton,
  component: MemberProfilePage,
});

// The cached member when there is one, else one fetch. `prefetchQuery`
// stores a failure in the cache instead of throwing, which is what the page
// wants: a 404 has to reach the branded NotFoundPage and anything else the
// retry surface, not the session error boundary a thrown loader error would
// land in.
async function resolveMember(
  queryClient: QueryClient,
  ref: string,
): Promise<MemberView | undefined> {
  const options = identityKeys.memberRef(ref);
  const cached = queryClient.getQueryData(options.queryKey);
  if (cached !== undefined) return cached;
  await queryClient.prefetchQuery(options);
  return queryClient.getQueryData(options.queryKey);
}

function MemberProfilePage() {
  const t = useTranslations('Profile');
  const fmt = useFormatDate();
  const { id } = Route.useParams();
  // The loader resolved (or failed) the lowercased ref; keying the same
  // way here shares that entry instead of opening a second one.
  const ref = id.toLowerCase();
  const viewer = useMember();

  // Resolver: on a uuid arrival this shares a key with the display query
  // below, so there is one fetch. On a handle arrival it is the only call
  // that can produce the uuid the collateral endpoints need. The loader
  // already ran the fetch, so a failure it left behind is not retried on
  // mount: the retry button refetches it on demand.
  const refQuery = useQuery({
    ...identityKeys.memberRef(ref),
    retryOnMount: false,
  });
  const resolvedId = refQuery.data?.id;
  const userId: UserId | null =
    resolvedId !== undefined
      ? asUserId(resolvedId)
      : isUuid(ref)
        ? asUserId(ref)
        : null;

  // Display query — always uuid-keyed, so mutation-side cache patches and
  // invalidations (which target `member(uuid)`) reach the page even when
  // it is handle-addressed. Seeded from the resolver so a handle arrival
  // renders in one load.
  // On a uuid arrival this key IS the resolver's, so it inherits the
  // loader's failure too and must not refetch it on mount either.
  const memberQuery = useQuery({
    ...identityKeys.member(userId ?? asUserId('')),
    enabled: userId !== null,
    retryOnMount: false,
    ...(refQuery.data !== undefined ? { initialData: refQuery.data } : {}),
  });

  const followingQuery = useQuery({
    ...identityKeys.follow(userId ?? asUserId('')),
    enabled: userId !== null,
  });
  const mutualsQuery = useQuery({
    ...identityKeys.memberMutuals(userId ?? asUserId('')),
    enabled: userId !== null,
  });
  // Member-scoped listings work for self and others alike: the server
  // authorizes a self listing by membership and scopes anyone else's to
  // what this viewer may discover (plus the member's "Show my Circles on
  // my profile" toggle). pageSize 100 = the complete-list window used by
  // the self profile aside (see /profile — the aside is a complete list,
  // not a teaser).
  const circlesQuery = useQuery({
    ...groupKeys.circle.forMember(userId ?? asUserId(''), { pageSize: 100 }),
    enabled: userId !== null,
  });
  const networksQuery = useQuery({
    ...groupKeys.network.forMember(userId ?? asUserId('')),
    enabled: userId !== null,
  });
  const postsQuery = useInfiniteAuthorPosts(
    userId ?? '',
    { pageSize: 5 },
    { enabled: userId !== null },
  );

  // Prefer the uuid-keyed entry: a mutation-side invalidation refreshes
  // that one, and the handle-keyed resolver entry would go stale.
  const memberData = memberQuery.data ?? refQuery.data;

  // Once per profile: the loader redirected before this mounted, so there
  // is one mount per visit.
  const viewedProfileIdRef = useRef<string | null>(null);
  const profileId = memberData?.id;
  useEffect(() => {
    if (profileId === undefined || viewer === null) return;
    if (viewedProfileIdRef.current === profileId) return;
    viewedProfileIdRef.current = profileId;
    trackProfileViewed({ is_own_profile: viewer.id === profileId });
  }, [profileId, viewer]);

  // A disabled query reports `isLoading: false`, so the handle arrival's
  // window — resolver in flight, display query not yet enabled — has to
  // come off the resolver, or it falls through to the error branch.
  const isLoadingMember =
    memberData === undefined && (refQuery.isLoading || memberQuery.isLoading);
  const memberError = refQuery.error ?? memberQuery.error;

  const showSkeleton = useDelayedLoading(isLoadingMember || viewer === null);
  if (showSkeleton) return <ProfileChromeSkeleton />;
  if (isLoadingMember || viewer === null) return null;
  // 404 → branded NotFoundPage (member doesn't exist or is deactivated).
  // Other failures keep the retry surface. We render the page directly
  // (rather than `throw notFound()` from a render-context useQuery, which
  // TanStack Router routes to its catch-boundary, not its 404 surface).
  // An unknown handle raises the same 404 as a member the viewer may not
  // see, so this branch covers both without distinguishing them.
  if (isNotFoundApiError(memberError)) return <NotFoundPage />;
  if (memberData === undefined) {
    return (
      <ErrorState
        title={t('errors.loadTitle')}
        body={t('errors.loadBody')}
        primary={<RetryButton />}
      />
    );
  }

  const isFollowing = followingQuery.data ?? false;
  const isViewer = viewer.id === memberData.id;

  const headlineRole = memberData.jobTitle ?? '';
  const headlineCompany = memberData.company ?? '';
  const headline =
    headlineRole && headlineCompany
      ? t('hero.headline', { role: headlineRole, company: headlineCompany })
      : headlineRole || headlineCompany || memberData.bio?.slice(0, 80) || '';

  // Built from the resolved member identity immediately — never gated on the
  // collateral queries. Each secondary region (posts, mutuals / circles /
  // networks) fills in independently and paints its own skeleton while in
  // flight, so a slow or failing collateral query can't freeze the profile.
  const mutuals = mutualsQuery.data ?? null;
  const posts = postsQuery.data?.pages.flatMap((p) => p.items) ?? [];
  const circles = circlesQuery.data?.items ?? [];
  const networks = networksQuery.data?.items ?? [];

  const header: ProfileHeaderData = {
    id: memberData.id,
    name:
      [memberData.firstName, memberData.lastName]
        .filter(Boolean)
        .join(' ')
        .trim() || t('memberFallback'),
    headline,
    location: memberData.location ?? '',
    memberSince: memberSinceFromCreatedAt(memberData.createdAt, fmt.monthYear),
    photo: memberData.photoUrl ?? undefined,
    photoLarge: memberData.photoLargeUrl ?? undefined,
    coverPhotoUrl: memberData.coverPhotoUrl ?? undefined,
    isViewer,
    isFollowing,
    isNetworkLeader: networks.some((n) => n.role === 'leader'),
  };

  const profile: Profile = mapProfileViewToProfile(memberData, isFollowing, {
    circles: circles.map((c) => ({
      id: c.id,
      name: c.name,
      role: c.role === 'leader' ? 'Leader' : 'Member',
      meta: t('circle.memberCount', { count: c.memberCount }),
      imageUrl: c.imageUrl,
    })),
    networks: networks.map((n) => ({
      id: n.id,
      name: n.name,
      role: n.role === 'leader' ? ('Leader' as const) : ('Member' as const),
      memberCount: n.memberCount,
      imageUrl: n.imageUrl,
    })),
    posts: posts.map((p) => authoredPostToFeedPost(p, fmt.relativeShort)),
    mutuals: (mutuals?.mutualMembers ?? []).slice(0, 6).map((m) => ({
      id: m.id,
      name: `${m.firstName} ${m.lastName}`.trim(),
      photo: m.photoUrl ?? '',
    })),
  }, t('memberFallback'), fmt.monthYear);

  // The aside shows circles + networks for every profile now; mutuals only
  // appear on someone else's.
  const asideLoading =
    circlesQuery.isLoading ||
    networksQuery.isLoading ||
    (!isViewer && mutualsQuery.isLoading);

  return (
    <ProfileChrome header={header}>
      <ProfileContentView
        profile={profile}
        postsLoading={postsQuery.isLoading}
        asideLoading={asideLoading}
        hasMorePosts={postsQuery.hasNextPage}
        loadingMorePosts={postsQuery.isFetchingNextPage}
        onLoadMorePosts={() => void postsQuery.fetchNextPage()}
      />
    </ProfileChrome>
  );
}
